/**
 * Per-currency locked price list (S3.1) — pure, zero I/O.
 *
 * Discribe-19 匯率變化 (chip 13), the rules this module encodes:
 * - The price matrix is a FIXED price list per currency (人工鎖定): an AUD 100
 *   price NEVER auto-converts — the USD figure is typed by a person (e.g.
 *   $70.00). There is deliberately NO conversion helper in this module and no
 *   FX input anywhere in its signatures.
 * - A currency with no locked row resolves UNRESOLVED with skipReason
 *   'no_currency_price' (plan-mandated trace vocabulary). The S5.1 matrix
 *   renders that state as glyph + text — never a blank cell, never a
 *   converted number.
 * - The daily FX table (`ExchangeRate`) serves 財務記帳與結算 ONLY; it is not
 *   part of `PricingDataset`, so the resolver cannot even see it.
 *
 * Lookup is EXACT-match on (productId, currency, priceLevelId): a level cell
 * never falls back to the list-price row — a missing level×currency price is
 * a missing price (the manual-lock doctrine, not a derivation hierarchy).
 */

import type { CurrencyCellResolution, CurrencyPriceRow } from '@/types/api/pricing';

/**
 * The locked row for (product, currency, level). `priceLevelId: null` selects
 * the list-price (no-level) row. Null when nobody typed a price for the cell.
 */
export function selectCurrencyPriceRow(
  rows: CurrencyPriceRow[] | undefined,
  productId: string,
  currency: string,
  priceLevelId: string | null
): CurrencyPriceRow | null {
  return (
    rows?.find(
      (r) =>
        r.productId === productId &&
        r.currency === currency &&
        (r.priceLevelId ?? null) === priceLevelId
    ) ?? null
  );
}

/**
 * Cell resolution for the PL × currency matrix (S5.1 consumer): resolved with
 * the locked row, or the explicit `no_currency_price` unresolved state.
 */
export function resolveCurrencyCell(
  rows: CurrencyPriceRow[] | undefined,
  productId: string,
  priceLevelId: string | null,
  currency: string
): CurrencyCellResolution {
  const row = selectCurrencyPriceRow(rows, productId, currency, priceLevelId);
  return row !== null
    ? { status: 'resolved', row }
    : { status: 'unresolved', skipReason: 'no_currency_price' };
}
